import './SectionGrid.scss';

import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  MenuItemDef,
} from '@amzn/gft-ag-grid-enterprise';
import { AurigaGrid } from '@components/Common/AurigaGrid/AurigaGrid';
import { globalGridDefaults } from '@constants/agGrid';
import { useAurigaContext } from '@context/AurigaContext';
import { useDecklinesContext } from '@context/DecklinesContext';
import { useFootnotesContext } from '@context/FootnotesContext';
import { useHighlightingContext } from '@context/HighlightingContext';
import { usePageContext } from '@context/PageContext';
import useHeaderTreeColumns from '@deckManagement/components/Page/hooks/useHeaderTreeColumns';
import useGridReady from '@deckManagement/hooks/useGridReady';
import { toAgGridContextMenuItems } from '@utils/pageHelpers';
import { find, isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DecklineRowData } from 'types/Page.types';
import { SectionGridProps } from 'types/SectionGrid.types';

/** Real rendered grid heights (px) used by the viewport-aware autoHeight decision. */
interface MeasuredGridHeights {
  rowHeight: number;
  headerHeight: number;
}

/**
 * Viewport-aware autoHeight decision (replaces the former fixed row-count threshold). A section grid
 * grows to fit its rows (`autoHeight`, no scrollbar) while its own content fits the available scroll
 * region; only a section whose content exceeds the region uses `normal` (fixed height — header stays
 * pinned, body scrolls). Each section is judged by its own measured content against the whole region —
 * a short section sizes to its content regardless of taller siblings (when several tall sections stack,
 * the page scrolls). Defaults to autoHeight until the region and grid heights are known. Pure (no DOM)
 * so it is unit-testable directly.
 *
 * @param availableHeight scroll-region height available to the grid (footnotes-independent
 *                        `gridScrollRegionHeight` from Page.tsx), or undefined when not yet measured
 * @param rowCount        rows this section renders
 * @param measured        real rendered row + header heights, or null when not yet measured
 */
export const isSectionAutoHeightEligible = (
  availableHeight: number | undefined,
  rowCount: number,
  measured: MeasuredGridHeights | null,
): boolean => {
  if (isNil(availableHeight) || !measured) return true;
  const contentHeight = measured.headerHeight + rowCount * measured.rowHeight;
  // Strict `<`: once content meets (not just exceeds) the available height, use `normal` — a
  // fixed-height grid WITH an internal scrollbar — rather than `autoHeight`, which has no scrollbar and
  // would clip the last row when content exactly fills the visible area.
  return contentHeight < availableHeight;
};

/**
 * SectionGrid
 *
 * Renders one `PageLayoutSection`: a nested AG Grid column hierarchy built from the section's
 * `columnHeaderTree` (via `useHeaderTreeColumns`), over row data supplied by the caller from
 * `getDecklines`. Page.tsx renders one instance per section, stacked top-to-bottom in `sections[]`
 * order. It wraps `AurigaGrid` (never `AgGridReact` directly) so the theme stays centrally enforced.
 *
 * Column building is a hook, so the per-section call has to happen in a per-section component rather
 * than in a loop. The pinned core columns (report-title stack, row number, KPI name) are passed in
 * rather than derived, since they are owned by Page.tsx.
 */
const SectionGrid = ({
  section,
  sectionLookupKey,
  grain,
  sectionRowData,
  coreColumnDefs,
  sectionGridOptions,
  getContextMenuOptions,
  contextMenuHandler,
  gridManager,
  isMultiSectionPage,
  availableHeight,
}: SectionGridProps) => {
  const { clearSubtotalHighlighting } = useHighlightingContext();
  const { pageRowData, currentGranularityPage } = usePageContext();
  const { selectedDeckline, chooseDeckline } = useDecklinesContext();
  const { setSelectedHeaderFootnotes } = useFootnotesContext();
  const { featureFlags } = useAurigaContext();
  // Reference to the AGGrid instance
  const gridApiRef = useRef<GridApi | null>(null);
  // Grid hook with utility functions
  const { onGridReady } = useGridReady(gridApiRef, {
    gridManager,
    sectionLookupKey,
  });
  // Previous selected Deckline id reference
  const prevSelectedDecklineIdRef = useRef<string | undefined>(undefined);
  // Avoid duplicate column fitting for height-only grid-size events.
  const previousGridWidthRef = useRef<number | null>(null);

  // A header footnote marker was clicked: drive the footnotes pane from that header (label +
  // its footnotes) and clear any deckline selection so the two footnote sources stay mutually
  // exclusive (a deckline click likewise clears the header selection — see the effect below).
  const handleFootnoteClick = useCallback(
    (label: string, footnotes: number[]) => {
      chooseDeckline(null);
      setSelectedHeaderFootnotes({ label, footnotes });
    },
    [chooseDeckline, setSelectedHeaderFootnotes],
  );

  // The section's nested column hierarchy, prefixed by the pinned core columns owned by Page.tsx.
  const headerTreeColumnDefs = useHeaderTreeColumns(section, grain, handleFootnoteClick, currentGranularityPage?.scale);
  const allColumnDefs = useMemo(
    () => [...coreColumnDefs, ...headerTreeColumnDefs],
    [coreColumnDefs, headerTreeColumnDefs],
  );

  const mergedGridOptions: GridOptions = {
    ...globalGridDefaults,
    ...sectionGridOptions,
    columnDefs: allColumnDefs,
    rowData: sectionRowData,
    // Opt back in to AG Grid's context menu, which `globalGridDefaults` suppresses for every other grid.
    suppressContextMenu: false,
  };

  /**
   * Builds the AG Grid right-click menu from Page.tsx's deckline menu options, replacing AG Grid's
   * default items (copy/export). Options and actions are bound to the right-clicked row (`params.node`),
   * not `selectedDeckline` — the selection change from the same right-click has not re-rendered yet
   * when AG Grid calls this, so `selectedDeckline` may still be the previously selected row.
   */
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<DecklineRowData>): (DefaultMenuItem | MenuItemDef)[] => {
      const targetDeckline = params.node?.data?.rawDecklineData ?? null;
      return toAgGridContextMenuItems(getContextMenuOptions(targetDeckline), featureFlags, (actionValue) =>
        contextMenuHandler(actionValue, targetDeckline),
      );
    },
    [getContextMenuOptions, featureFlags, contextMenuHandler],
  );

  /**
   * Ensures that AGGrid is focused on the correct row when `selectedDeckline` changes.
   */
  useEffect(() => {
    // Only clear highlighting when moving to a different row
    const currentDecklineId = selectedDeckline?.deckline_id;
    if (prevSelectedDecklineIdRef.current && prevSelectedDecklineIdRef.current !== currentDecklineId) {
      // Clear highlights for subtotals when no longer relevant, based on previous deckline selected
      const prevDeckline = find(
        pageRowData,
        (row) => row.rawDecklineData.deckline_id === prevSelectedDecklineIdRef.current,
      );

      // Only clear highlighting if the previous deckline had a subtotal formula
      if (
        prevDeckline &&
        !isNil(prevDeckline?.rawDecklineData.subtotal_formula) &&
        !isEmpty(prevDeckline?.rawDecklineData.subtotal_formula)
      ) {
        // Important*: Clear highlighting depends on the full PageRowData, not a section subset
        clearSubtotalHighlighting(pageRowData);
      }
    }
    prevSelectedDecklineIdRef.current = currentDecklineId;

    if (selectedDeckline) {
      const targetRowIndex = sectionRowData.findIndex(
        (row) => row.rawDecklineData.deckline_id === selectedDeckline.deckline_id,
      );
      // Only set focus if the rowIndex doesn't match and exists in this Section's grid.
      if (targetRowIndex >= 0 && gridApiRef.current?.getFocusedCell()?.rowIndex !== targetRowIndex) {
        gridApiRef.current?.setFocusedCell(targetRowIndex, 'kpi_name');
      }
    }
  }, [sectionRowData, selectedDeckline, clearSubtotalHighlighting, pageRowData]);

  // Real rendered heights measured from the grid (not estimated): the theme row height (from the grid
  // API) and the full multi-row header height (the rendered `.ag-header` element). Captured once the
  // grid first renders and refreshed on resize, so the autoHeight decision uses actual layout values.
  const [measuredHeights, setMeasuredHeights] = useState<MeasuredGridHeights | null>(null);

  /** Measure the grid's real row + header height from the rendered grid, for the autoHeight decision. */
  const measureGridHeights = useCallback((api: GridApi) => {
    // Guard the AG Grid API calls: if unavailable, leave heights unmeasured so the decision defaults to
    // autoHeight rather than throwing during render.
    if (typeof api.getSizesForCurrentTheme !== 'function' || typeof api.getGridId !== 'function') return;
    const { rowHeight } = api.getSizesForCurrentTheme();
    // The section header is a multi-row stack (title + block bands + leaf row); measure the rendered
    // header element rather than the theme's single-row headerHeight.
    const headerEl = document.querySelector<HTMLElement>(`[grid-id="${api.getGridId()}"] .ag-header`);
    const headerHeight = headerEl?.offsetHeight ?? 0;
    if (rowHeight > 0 && headerHeight > 0) {
      setMeasuredHeights((prev) =>
        prev?.rowHeight === rowHeight && prev?.headerHeight === headerHeight ? prev : { rowHeight, headerHeight },
      );
    }
  }, []);

  /** Viewport-aware autoHeight decision — see {@link isSectionAutoHeightEligible}. */
  const isAutoHeightEligible = useMemo(
    () => isSectionAutoHeightEligible(availableHeight, sectionRowData.length, measuredHeights),
    [availableHeight, measuredHeights, sectionRowData.length],
  );

  /**
   *  Note: 'domLayout' auto grows a grid to its content height, which is an expensive compromise, as it
   *  forces the grid to load all rows in memory to achieve the resize. It is therefore chosen only when
   *  the content actually fits the available scroll region (see isSectionAutoHeightEligible) — a bounded
   *  row count; a section whose content would fill/overflow the region uses 'normal' (fixed height with
   *  an internal scrollbar) instead, so no row is clipped and tall sections do not load everything.
   *  See: https://www.ag-grid.com/react-data-grid/grid-size/
   */
  return (
    <AurigaGrid
      {...mergedGridOptions}
      className={`section-grid-container ${isMultiSectionPage && isAutoHeightEligible ? 'multi-section-auto' : ''}`}
      domLayout={isAutoHeightEligible ? 'autoHeight' : 'normal'}
      onGridReady={onGridReady}
      getContextMenuItems={getContextMenuItems}
      // Render the menu at the document body so short (autoHeight) grids do not clip it.
      popupParent={document.body}
      onGridSizeChanged={(params) => {
        // Re-measure real heights on size changes so the autoHeight decision tracks layout changes.
        measureGridHeights(params.api);
        const gridWidth = params.clientWidth;
        if (gridWidth <= 0 || previousGridWidthRef.current === gridWidth) return;
        previousGridWidthRef.current = gridWidth;
        // Apply the same sparse-only policy as initial render. sizeColumnsToFit would force dense
        // sections to shrink into the viewport on a monitor/window resize, losing horizontal scroll.
        params.api.autoSizeAllColumns({
          skipHeader: true,
          scaleUpToFitGridWidth: true,
        });
      }}
      // Auto-size section row columns in the section view, once data has loaded
      onFirstDataRendered={(params) => {
        // Capture the real rendered row + header heights for the viewport-aware autoHeight decision.
        measureGridHeights(params.api);
        // AG Grid auto-size completes asynchronously. Its built-in scaleUpToFitGridWidth option runs
        // the viewport fill after content measurement, avoiding the right-side gutter on sparse
        // sections without shrinking dense sections that need horizontal scrolling. Columns size to
        // their cells, not their header, so a long leaf label wraps instead of widening its column.
        params.api.autoSizeAllColumns({
          skipHeader: true,
          scaleUpToFitGridWidth: true,
        });

        // Scroll to the selected deckline if one exists (e.g., after grid re-initialization)
        if (selectedDeckline) {
          const targetRowIndex = sectionRowData.findIndex(
            (row) => row.rawDecklineData.deckline_id === selectedDeckline.deckline_id,
          );
          if (targetRowIndex >= 0) {
            params.api.ensureIndexVisible(targetRowIndex, 'middle');
          }
        }
      }}
    />
  );
};

export default SectionGrid;
